/**
 * ホーム(`/portal`)。「要対応の受信箱」として、今日やることを 1 画面に集める。
 *
 * ① 要対応カード 4 枚 ② あなたの対応待ち(混在リストの上位 5 件)③ アプリ
 * ④ SSO サービス。データは**既存 API の組み合わせ**で出す(新しい endpoint は無し)。
 *
 * 使えないアプリ(ロール無し / 契約外)の分は**叩かない**(`me.apps` と `hasFeature`
 * で先に絞る。403 を出さない)。各クエリは独立で、1 つ落ちても他は出す。
 *
 * 「アプリ」= この基盤の中の画面(apps テーブル・ロールで絞られる)。
 * 「サービス」= SSO で入る外の SaaS(R7a のサービスプロバイダー)。
 * サービスは**ロールに関係なく全員に出す**(SSO の入口なので、ID管理の権限とは別)。
 */
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Spin, Tag } from "antd";
import { useEffect, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import type { Device, Me, WfRequest, WfTask } from "../lib/api";
import { hasFeature } from "../lib/features";
import { hasRole } from "../lib/roles";
import { jst } from "../lib/format";
import { useApi, useMe } from "../lib/useApi";
import { appIcon } from "./appIcons";
import "./home.css";

/** 30 日以上チェックインが無い端末を「注意」とする */
const STALE_DAYS = 30;
const MAX_INBOX = 5;
/** 端末は一覧 API を使うので、この台数までしか数えない(超えたら「先頭 N 台のうち」と出す。UX1-a 中1) */
const DEVICE_SCAN = 200;

function can(me: Me | undefined, app: string, feature: string | string[]): boolean {
  return !!me && me.apps.includes(app) && hasFeature(me, feature);
}

/** `wfHooks.isOverdue` と同じ判定。shell → apps の依存を作らないためここに置く */
const isOverdue = (sla: { over: boolean; done: boolean } | undefined) => !!sla?.over && !sla.done;

function isStale(d: Device, now: number): boolean {
  if (!d.last_seen) return true;
  const t = Date.parse(d.last_seen);
  return Number.isNaN(t) || now - t > STALE_DAYS * 86_400_000;
}

function greeting(hour: number): string {
  if (hour < 11) return "おはようございます";
  if (hour < 18) return "こんにちは";
  return "こんばんは";
}

type Inbox = {
  key: string;
  chip: "approval" | "task" | "diff" | "device";
  chipLabel: string;
  main: string;
  sub: string;
  right: string;
  danger: boolean;
  to: string;
  /** 並び: 期限超過が先。同 rank 内は期限の近い順 */
  rank: number;
  /** 期限までの残り時間(SLA が無い行・集約行は Infinity で最後) */
  remain: number;
};

const remaining = (sla: { remaining_hours: number } | undefined) =>
  sla && Number.isFinite(sla.remaining_hours) ? sla.remaining_hours : Infinity;

export function Home() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  const m = me.data;

  const apps = useQuery({ queryKey: ["apps"], queryFn: api.apps, staleTime: 60_000 });

  // ---- 要対応(使えるアプリの分だけ叩く。各クエリは独立) --------------------
  const wantApprovals = can(m, "hr", "hr.workflow");
  // キーは各アプリのフック(`wfHooks.useRequests` など)と**同じ形**にする。アプリ側の
  // invalidate(`["hr"]` / `["idp"]` / `["mdm"]`)がホームにも効き、操作して戻っても古くならない
  // (UX1-a 中2 / 低15)。params の形(`{ status: "pending" }`)まで揃えないと別キーになる
  const approvals = useQuery({
    queryKey: ["hr", "wf-requests", { status: "pending" }],
    queryFn: () => api.wfRequests(new URLSearchParams({ limit: "100", status: "pending" })),
    enabled: wantApprovals,
    staleTime: 30_000,
  });
  const tasks = useQuery({
    queryKey: ["hr", "wf-tasks", { status: "pending" }],
    queryFn: () => api.wfTasks(new URLSearchParams({ limit: "100", status: "pending" })),
    enabled: wantApprovals,
    staleTime: 30_000,
  });
  const wantEmployees = can(m, "hr", "hr.directory");
  const employees = useQuery({
    queryKey: ["hr", "employees", "count"],
    queryFn: () => api.employees(new URLSearchParams({ limit: "1" })),
    enabled: wantEmployees,
    staleTime: 30_000,
  });
  const wantRecon = can(m, "idp", "idp.reconcile");
  const recon = useQuery({
    queryKey: ["idp", "reconciliation"],
    // 1 度も突合していなければ API は 204(undefined)。react-query v5 は undefined を
    // エラー扱いにするので null に倒し、「まだ実行していない」の分岐へ入れる
    queryFn: async () => (await api.reconciliation()) ?? null,
    enabled: wantRecon,
    staleTime: 30_000,
    retry: false,
  });
  const wantIdpUsers = can(m, "idp", "idp.users");
  const idpUsers = useQuery({
    queryKey: ["idp", "users", "count"],
    queryFn: () => api.idpUsers(new URLSearchParams({ limit: "1" })),
    enabled: wantIdpUsers,
    staleTime: 30_000,
    retry: false,
  });
  const wantDevices = can(m, "mdm", ["mdm.apple", "mdm.windows", "mdm.android"]);
  const devices = useQuery({
    queryKey: ["mdm", "devices", { limit: DEVICE_SCAN }],
    queryFn: () => api.devices(new URLSearchParams({ limit: String(DEVICE_SCAN) })),
    enabled: wantDevices,
    staleTime: 30_000,
  });

  // パスワードの決まりの説明(ドメイン設定で入れていなければ空。落ちても無視する)
  const passwordNote = useQuery({
    queryKey: ["idp", "password-note"],
    queryFn: api.idpPasswordNote,
    staleTime: 300_000,
    retry: false,
  });
  // 落ちてもホームは出す(サービスの節が消えるだけ)
  const services = useQuery({
    queryKey: ["portal", "services"],
    queryFn: api.portalServices,
    staleTime: 60_000,
    retry: false,
  });

  // `/portal#sso` などで来たら、その節まで送る(ナビの「SSO サービス」から)
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    el?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [location.hash, apps.data, services.data, passwordNote.data]);

  if (apps.isLoading || me.isLoading)
    return (
      <div className="ux-home" style={{ alignItems: "center" }}>
        <Spin />
      </div>
    );

  if (apps.isError)
    return (
      <Alert
        type="error"
        showIcon
        style={{ margin: 24 }}
        message="アプリの一覧を取得できなかった"
        description={apps.error instanceof Error ? apps.error.message : String(apps.error)}
      />
    );

  const now = Date.now();
  const items = apps.data?.items ?? [];
  const tiles = services.data?.items ?? [];

  // ---- 数字 ------------------------------------------------------------------
  // 共有キーなので、feature を外した後もキャッシュに行が残り得る。表示側も flag で絞る(UX1-a 中3)
  const approvalRows: WfRequest[] = wantApprovals ? (approvals.data?.items ?? []) : [];
  const taskRows: WfTask[] = wantApprovals ? (tasks.data?.items ?? []) : [];
  const approvalTotal = approvals.data?.total;
  const taskTotal = tasks.data?.total;
  const approvalOverdue = approvalRows.filter((r) => isOverdue(r.sla)).length;
  const taskOverdue = taskRows.filter((r) => isOverdue(r.sla)).length;
  const findings = wantRecon ? recon.data?.findings : undefined;
  const deviceRows: Device[] = wantDevices ? (devices.data?.items ?? []) : [];
  const staleDevices = wantDevices && devices.data ? deviceRows.filter((d) => isStale(d, now)).length : undefined;
  // 一覧 API は先頭 DEVICE_SCAN 台しか返さない。それを超える台数のときは確定件数として出さない(中1)
  const devicePartial = !!devices.data && devices.data.total > deviceRows.length;

  // ---- 要対応カード ------------------------------------------------------------
  type Card = {
    key: string;
    label: string;
    to: string;
    value: number | undefined;
    error: boolean;
    note?: ReactNode;
    noteKind?: "warn" | "danger";
  };
  const cards: Card[] = [];
  if (wantApprovals) {
    cards.push({
      key: "approvals",
      label: "承認待ち",
      to: "/hr/approvals",
      value: approvalTotal,
      error: approvals.isError,
      note: approvalOverdue > 0 ? `うち期限超過 ${approvalOverdue} 件` : "期限超過なし",
      noteKind: approvalOverdue > 0 ? "danger" : undefined,
    });
    cards.push({
      key: "tasks",
      // `/api/hr/tasks` は担当者で絞れない(全社の未完了)。数字に合わせた名前にする
      label: "未完了のタスク",
      to: "/hr/tasks",
      value: taskTotal,
      error: tasks.isError,
      note: taskOverdue > 0 ? `うち期限超過 ${taskOverdue} 件` : "期限超過なし",
      noteKind: taskOverdue > 0 ? "danger" : undefined,
    });
  }
  if (wantRecon) {
    cards.push({
      key: "recon",
      label: "アカウント突合の差分",
      to: "/idp/reconciliation",
      value: recon.isSuccess ? (findings ?? 0) : undefined,
      error: recon.isError,
      note: recon.data?.finished_at ? `最終実行 ${jst(recon.data.finished_at)}` : "まだ実行していない",
      noteKind: findings ? "warn" : undefined,
    });
  }
  if (wantDevices) {
    cards.push({
      key: "devices",
      label: "注意が要る端末",
      to: "/mdm/devices",
      value: staleDevices,
      error: devices.isError,
      note: devicePartial
        ? `${STALE_DAYS} 日以上チェックインなし(先頭 ${deviceRows.length} 台のうち)`
        : `${STALE_DAYS} 日以上チェックインなし`,
      noteKind: staleDevices ? "warn" : undefined,
    });
  }
  // 挨拶の件数 = カードの数字の合計(単位を揃える。差分・端末も件数で足す)
  const pending = cards.reduce((sum, c) => sum + (c.value ?? 0), 0);

  // ---- 対応待ちリスト(混在、期限超過が先 → 期限の近い順、集約行は後ろ、上位 5 件) ---
  const inbox: Inbox[] = [];
  for (const r of approvalRows) {
    const over = isOverdue(r.sla);
    inbox.push({
      key: `a${r.id}`,
      chip: "approval",
      chipLabel: "承認",
      main: `${r.kind_label} — ${r.employee_name}`,
      sub: `申請者 ${r.requested_by}${r.start_date ? ` · ${r.start_date}` : ""}`,
      right: over ? "期限超過" : jst(r.created_at),
      danger: over,
      to: `/hr/wf-requests/${r.id}`,
      rank: over ? 0 : 2,
      remain: remaining(r.sla),
    });
  }
  for (const t of taskRows) {
    const over = isOverdue(t.sla);
    inbox.push({
      key: `t${t.id}`,
      chip: "task",
      chipLabel: "タスク",
      main: t.employee_name ? `${t.name} — ${t.employee_name}` : t.name,
      sub: t.op_label || t.action_label || t.request_kind,
      right: over ? "期限超過" : t.owner || "",
      danger: over,
      to: t.request_id ? `/hr/wf-requests/${t.request_id}` : "/hr/tasks",
      rank: over ? 0 : 3,
      remain: remaining(t.sla),
    });
  }
  if (findings) {
    inbox.push({
      key: "recon",
      chip: "diff",
      chipLabel: "差分",
      main: `アカウント突合の差分 ${findings} 件`,
      sub: recon.data?.finished_at ? `最終実行 ${jst(recon.data.finished_at)}` : "",
      right: "",
      danger: false,
      to: "/idp/reconciliation",
      rank: 4,
      remain: Infinity,
    });
  }
  if (staleDevices) {
    inbox.push({
      key: "devices",
      chip: "device",
      chipLabel: "端末",
      main: `${STALE_DAYS} 日以上チェックインが無い端末 ${staleDevices} 台${devicePartial ? "以上" : ""}`,
      sub: "",
      right: "",
      danger: false,
      to: "/mdm/devices",
      rank: 4,
      remain: Infinity,
    });
  }
  inbox.sort((a, b) => a.rank - b.rank || a.remain - b.remain);
  const inboxTop = inbox.slice(0, MAX_INBOX);
  // 「すべて見る」は種別ごと(混在リストを承認一覧 1 つに固定しない。UX1-a 中5)
  const inboxAll: { label: string; to: string }[] = [
    ...(wantApprovals ? [{ label: "承認", to: "/hr/approvals" }, { label: "タスク", to: "/hr/tasks" }] : []),
    ...(wantRecon ? [{ label: "差分", to: "/idp/reconciliation" }] : []),
    ...(wantDevices ? [{ label: "端末", to: "/mdm/devices" }] : []),
  ];

  // ---- アプリの 1 行要約(取れない要約は出さない) -------------------------------
  const summaryOf = (key: string): string | null => {
    if (key === "hr") {
      const parts: string[] = [];
      if (employees.data) parts.push(`従業員 ${employees.data.total} 人`);
      if (approvals.data) parts.push(`申請 ${approvals.data.total} 件が承認待ち`);
      return parts.length ? parts.join(" · ") : null;
    }
    if (key === "idp") {
      const parts: string[] = [];
      if (idpUsers.data) parts.push(`ユーザー ${idpUsers.data.total} 人`);
      if (recon.isSuccess) parts.push(`突合差分 ${findings ?? 0} 件`);
      return parts.length ? parts.join(" · ") : null;
    }
    if (key === "mdm") {
      const parts: string[] = [];
      if (devices.data) parts.push(`端末 ${devices.data.total} 台`);
      // 先頭 DEVICE_SCAN 台で打ち切ったときは注意件数を断定しない(要対応カードに「先頭 N 台のうち」がある。fix1 中6)
      if (staleDevices !== undefined) parts.push(devicePartial ? "注意は要対応カードを見る" : `注意 ${staleDevices} 台`);
      return parts.length ? parts.join(" · ") : null;
    }
    return null;
  };

  const today = new Date();
  const dateText = today.toLocaleDateString("ja-JP", {
    month: "long",
    day: "numeric",
    weekday: "short",
  });
  const surname = (m?.display_name || m?.username || "").split(/\s+/)[0];
  const anyLoading = [approvals, tasks, recon, devices].some((q) => q.isFetching && !q.data);

  return (
    <div className="ux-home">
      <div className="ux-home-head">
        <div>
          <h1>
            {greeting(today.getHours())}
            {surname ? `、${surname}さん` : ""}
          </h1>
          <p data-testid="home-greeting">
            {dateText}。
            {anyLoading
              ? "対応待ちを数えている…"
              : cards.length > 0
                ? `対応待ちが ${pending} 件あります。`
                : "対応待ちはありません。"}
          </p>
        </div>
        {wantApprovals && hasRole(m, "hr:operator") && (
          <Button type="primary" size="large" onClick={() => navigate("/hr/requests/new")}>
            新しい申請
          </Button>
        )}
      </div>

      {items.length === 0 && (
        <Alert
          type="warning"
          showIcon
          message="使えるアプリが無い"
          description="ロールが1つも付いていない。管理者に依頼してください。"
        />
      )}

      {cards.length > 0 && (
        <section className="ux-todo" aria-label="要対応">
          {cards.map((c) => (
            <Link key={c.key} to={c.to} className="ux-todo-card" data-testid="todo-card">
              <span className="ux-todo-label">{c.label}</span>
              <span className="ux-todo-num" data-testid="todo-num">
                {c.error ? "—" : c.value === undefined ? <Spin size="small" /> : c.value}
              </span>
              <span className={`ux-todo-note${c.noteKind ? ` is-${c.noteKind}` : ""}`}>
                {c.error ? "取得できない" : c.note}
              </span>
            </Link>
          ))}
        </section>
      )}

      <div className="ux-home-grid">
        <div className="ux-side">
          {cards.length > 0 && (
            <section className="ux-panel" aria-label="要対応" data-testid="inbox">
              <div className="ux-panel-head">
                {/* 上のカード帯と同じ語(「あなたの」は owner で絞れていないので付けない。UX1-a 低A) */}
                <h2>要対応</h2>
                <span className="ux-inbox-all">
                  {inboxAll.map((l, i) => (
                    <span key={l.to}>
                      {i > 0 && <span aria-hidden> · </span>}
                      <Link to={l.to}>{l.label}をすべて見る</Link>
                    </span>
                  ))}
                </span>
              </div>
              {anyLoading && inboxTop.length === 0 ? (
                <div className="ux-inbox-empty">
                  <Spin size="small" />
                </div>
              ) : inboxTop.length === 0 ? (
                <div className="ux-inbox-empty">いま対応が要るものは無い</div>
              ) : (
                inboxTop.map((row) => (
                  <Link key={row.key} to={row.to} className="ux-inbox-row">
                    <span className={`ux-chip is-${row.chip}`}>{row.chipLabel}</span>
                    <span className="ux-inbox-main">
                      {row.main}
                      {row.sub && <span className="ux-inbox-sub">{row.sub}</span>}
                    </span>
                    {row.right && (
                      <span className={`ux-inbox-right${row.danger ? " is-danger" : ""}`}>
                        {row.right}
                      </span>
                    )}
                  </Link>
                ))
              )}
            </section>
          )}

          {/* ナビ「自分のアカウント」の飛び先。決まりが無くても節は出す */}
          <section className="ux-panel" id="account" aria-label="自分のアカウント">
            <div className="ux-panel-head">
              <h2>自分のアカウント</h2>
            </div>
            <div className="ux-panel-body" data-testid="account-note">
              {passwordNote.isError ? (
                <p className="ux-account-note ux-muted">パスワードの決まりを取得できなかった。</p>
              ) : passwordNote.isLoading ? (
                <p className="ux-account-note ux-muted">
                  <Spin size="small" />
                </p>
              ) : passwordNote.data?.note ? (
                <p className="ux-account-note">{passwordNote.data.note}</p>
              ) : (
                <p className="ux-account-note ux-muted">
                  {m?.display_name || m?.username}
                  {m?.display_name && m.username ? `(${m.username})` : ""}
                  。パスワードの決まりは設定されていない。
                </p>
              )}
            </div>
          </section>
        </div>

        <div className="ux-side">
          <section className="ux-panel" aria-label="アプリ">
            <div className="ux-panel-head">
              <h2>アプリ</h2>
            </div>
            <div className="ux-apps-list" data-testid="app-tiles">
              {items.map((a) => {
                const ready = a.status === "available";
                const summary = ready ? summaryOf(a.key) : null;
                return (
                  <Card
                    key={a.key}
                    size="small"
                    hoverable={ready}
                    className={`ux-app-tile${ready ? "" : " is-disabled"}`}
                    onClick={ready ? () => navigate(a.path) : undefined}
                    data-testid={`app-tile-${a.key}`}
                  >
                    <span className={`ux-app-icon is-${a.key}`}>{appIcon(a.key)}</span>
                    <span className="ux-app-text">
                      <span className="ux-app-name">
                        {a.name}
                        {!ready && <Tag>準備中</Tag>}
                      </span>
                      <span className="ux-app-sum" title={summary ?? a.description}>
                        {summary ?? a.description}
                      </span>
                    </span>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* ナビ「SSO サービス」の飛び先。0 件でも節は出す */}
          <section className="ux-panel" id="sso" aria-label="SSO サービス">
            <div className="ux-panel-head">
              <h2>SSO で開く</h2>
            </div>
            {services.isError ? (
              <div className="ux-inbox-empty">SSO サービスの一覧を取得できなかった</div>
            ) : services.isLoading ? (
              <div className="ux-inbox-empty">
                <Spin size="small" />
              </div>
            ) : tiles.length === 0 ? (
              <div className="ux-inbox-empty">SSO で開くサービスはまだ無い</div>
            ) : (
              <div className="ux-sso-grid" data-testid="service-tiles">
                {tiles.map((s) => (
                  <Card
                    key={s.id}
                    size="small"
                    hoverable
                    className="ux-sso-tile"
                    onClick={() => window.open(s.url, "_blank", "noopener")}
                  >
                    <span className="ux-sso-logo">
                      {s.logo ? <img src={s.logo} alt="" /> : [...s.display_name][0]}
                    </span>
                    <span className="ux-sso-name" title={s.display_name}>
                      {s.display_name}
                    </span>
                  </Card>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
